import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function CopyToCookbookButton({ recipe, className = "", compact = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [books, setBooks] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!recipe?.recipe_id || (user && user.user_id === recipe.owner_id)) return null;

  const onOpenChange = async (o) => {
    if (o && !user) { nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`); return; }
    setOpen(o);
    if (o && !books) {
      try {
        const { data } = await api.get("/cookbooks");
        setBooks([...(data.owned || []), ...(data.co_edited || [])]);
      } catch { setBooks([]); }
    }
  };

  const copy = async (cb) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/copy`, { target_cookbook_id: cb.cookbook_id });
      toast.success(`Copied to "${data.target_title || cb.title}"`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`copy-to-cookbook-${recipe.recipe_id}`}
          onClick={(e) => e.stopPropagation()}
          className={className || "btn-outline-ink !py-2.5 !px-4 text-sm"}
        >
          <Copy size={compact ? 14 : 16} weight="bold" /> {compact ? "Copy" : "Copy to my cookbook"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid="copy-to-cookbook-menu">
        <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-rf-ink-soft">Copy to…</p>
        {books === null && <p className="px-2 py-2 text-sm text-rf-ink-soft">Loading…</p>}
        {books?.length === 0 && <p className="px-2 py-2 text-sm text-rf-ink-soft">No cookbooks yet.</p>}
        {books?.map((c) => (
          <button
            key={c.cookbook_id}
            type="button"
            disabled={busy}
            data-testid={`copy-to-cookbook-option-${c.cookbook_id}`}
            onClick={() => copy(c)}
            className="block w-full text-left px-3 py-2 hover:bg-rf-cream-alt rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {c.title}{c.is_system ? <span className="text-xs text-rf-ink-soft ml-2">(Inbox)</span> : null}
          </button>
        ))}
        <p className="px-2 pt-2 text-[11px] text-rf-ink-soft leading-snug">Copies stay private and don&apos;t use up your free recipes unless you edit them.</p>
      </PopoverContent>
    </Popover>
  );
}
